import React, { useEffect, useState } from "react";
import { isEqual } from "lodash";
import { Locale } from "@linkfluence/models/locale";
import { usePrevious } from "@lkf/hooks";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import Subtitle from "@radarly/components/cards/card/subtitle";
import { AI_SUMMARY_QUERY_KEY } from "@radarly/core/ai/use-cases/useAISummary";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { AICard } from "@radarly/models/cards/model";
import { areClausesEqual } from "@radarly/models/clause/is";
import { AIFragment } from "@radarly/models/search";
import Date from "@radarly/shared/cards/subtitle/date";
import Tile from "@radarly/shared/cards/tile";
import AI from "@radarly/shared/widgets/ai";
import { useFragment } from "../fragment";
import { CardProps } from "../model";

const RadarlyAiCard = ({
  card,
  height,
  clause,
  from,
  to,
  nowDate,
  timezone,
  isWizard,
}: CardProps<AICard>) => {
  const project = useCurrentProject();

  const fragment = useFragment(card.fragments[0], clause, from, to, timezone);

  const locale = useCurrentUserLocale();
  const [previousFragment, setPreviousFragment] =
    useState<AIFragment>(undefined);

  const [previousLocale, setPreviousLocale] = useState<Locale>(locale);

  // Fix needReload issues on save on Insight Page
  const isFetchingIP = useIsFetching({ queryKey: ["/insight-page/get"] });
  const previousIsFetchingIP = usePrevious(isFetchingIP);
  const hasFetchedIp = isFetchingIP < previousIsFetchingIP;

  const isPreviousFragmentDifferent =
    previousFragment &&
    (!isEqual(previousFragment.analysis, fragment.analysis) ||
      !isEqual(previousFragment.context, fragment.context) ||
      !areClausesEqual(
        previousFragment?.request?.query,
        fragment?.request?.query
      ));
  const isSameLocale = isEqual(previousLocale, locale);

  const shouldSetPreviousFragment =
    previousFragment === undefined || hasFetchedIp;

  const bypassNeedReload = isWizard && isSameLocale;

  useEffect(() => {
    if (shouldSetPreviousFragment) {
      setPreviousFragment(fragment);
    }
  }, [fragment, shouldSetPreviousFragment]);

  const needReload =
    previousFragment &&
    ((isPreviousFragmentDifferent && !isWizard) || !isSameLocale);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (needReload) {
      queryClient.cancelQueries({
        queryKey: AI_SUMMARY_QUERY_KEY({
          pid: project?.id,
          analysis: previousFragment?.analysis,
          context: previousFragment?.context,
          request: previousFragment?.request,
          locale: previousLocale,
        }),
      });
    }
  }, [
    needReload,
    previousFragment?.analysis,
    previousFragment?.context,
    previousFragment?.request,
    project?.id,
    queryClient,
    previousLocale,
  ]);

  return (
    <Tile
      title={card.title}
      height={height}
      subtitle={
        <Subtitle
          meta={[
            <Date fragment={fragment} timezone={timezone} nowDate={nowDate} />,
          ]}
        />
      }
      needReload={needReload}
      onClickReload={() => {
        setPreviousFragment(fragment);
        setPreviousLocale(locale);
      }}
      clause={fragment.request.query}
      isAI
    >
      <AI
        fragments={
          bypassNeedReload ? [fragment] : [previousFragment ?? fragment]
        }
        project={project}
        dataviz={card.dataviz}
        overflowTitleCard
        isAlreadyOverlay={needReload}
        previousLocale={previousLocale}
      />
    </Tile>
  );
};

export default RadarlyAiCard;
